import { Box, Card, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import i18n from '../../../i18n';
import TableComponent, { HeadCell } from '../../../component/table/table.component';
import { formatEUR } from '../../../constants/tool.fn';
import { useCallback, useEffect } from 'react';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import StaffDataStore from './staff-data-state';
import dataAnalysisStore from '../data-analysis-state';
import { ErrorMessageBox } from '../data-analysis.page';
import { useTranslation } from 'react-i18next';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import GlobalNotification from '../../../component/common/snackbar';
import './scss/staff-data.style.scss';

const StaffDataPage = observer(() => {
    useTranslation();
    const staffDataStore = useLocalObservable(() => new StaffDataStore());
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    // 当前时间段筛选
    const startDate = dataAnalysisStore.startDate
    const endDate = dataAnalysisStore.endDate
    useEffect(() => {
        staffDataStore.fetchData(stringId, startDate, endDate)
    }, [stringId, startDate, endDate])

    /** 与导出同源：列表全空则提示、不导空表 */
    const preCheckExport = useCallback((): boolean => {
        if (!staffDataStore.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [staffDataStore.data?.length]);

    const handleExport = useCallback(() => {
        staffDataStore.downloadExcel();
    }, [staffDataStore]);

    /**
     * 当前排序状态
     * @param key
     * @returns
     */
    const getSortStatus = (key: string) => {
        const sort = staffDataStore.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1
        }
        return 0
    }
    // 列表项额外费用
    /** 表头列配置（按文案长度设 minWidth，窄屏横向滚动、不挤压列宽） */
    const headCellListStaff: HeadCell[] = [
        {
            id: 'order_employee_name',
            label: i18n.t('business_analysis_page_employee_name'),
            minWidth: '200px',
        },
        {
            id: 'quantity',
            label: i18n.t('business_analysis_page_refund_quantity'),
            minWidth: '140px',
            sortStatus: getSortStatus('quantity'),
            // 仅展示退菜数量，不显示相对占比进度条
            renderFunction: (item) => <span>{item.quantity}</span>
        },
        {
            id: 'price_total',
            label: i18n.t('business_analysis_page_text_sum_price'),
            minWidth: '160px',
            sortStatus: getSortStatus('price_total'),
            // 仅展示价格合计，不显示相对占比进度条
            renderFunction: (item) => (
                <span>{formatEUR(+(item?.price_total || 0))}</span>
            )
        }
    ]


    /**
     * 排序
     * @param headCell
     */
    const handleSort = (headCell: HeadCell) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break;
            case 1:
                sortStatus = 'ASC'
                break;
            case 2:
                sortStatus = ''
                break;
            default:
                sortStatus = ''
                break;
        }
        const value = {
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        }
        staffDataStore.setSortValue(value)
    }

    /** 顶部栏：标题 + 下载图标位 + 日期筛选 */
    const renderTopBar = () => (
        <Box className='staff-data-cloud-top-bar'>
            <Typography variant='h3' sx={{ color: 'var(--text-dark)' }}>
                {i18n.t('business_analysis_page_msg_fetch_employee_analysis')}
            </Typography>
            <Box className='staff-data-cloud-top-actions'>
                <DataExportIconButton
                    title={i18n.t('tunnel_management_page_export')}
                    disabled={staffDataStore.loading}
                    preCheck={preCheckExport}
                    onExport={handleExport}
                />
                <PeriodDateFilter
                    startDate={dataAnalysisStore.startDate}
                    endDate={dataAnalysisStore.endDate}
                    pickerValue={dataAnalysisStore.pickerValue}
                    onDateTimeChange={dataAnalysisStore.handleDateTime}
                />
            </Box>
        </Box>
    )

    return (
        staffDataStore.errorMessage ?
            <ErrorMessageBox errorMessage={staffDataStore.errorMessage} /> :
            <Box className='staff-data-page'>
                <Card
                    className='staff-data-card-cloud'
                    sx={{
                        marginTop: '16px',
                        padding: '24px 16px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {renderTopBar()}
                    <TableComponent
                        extraClass='staff-data-cloud-table'
                        // 分页
                        isPagination
                        allCount={staffDataStore.data?.length}
                        // 列表项
                        headCellList={headCellListStaff}
                        // 列表数据
                        list={staffDataStore.data}
                        // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
                        showChecked={0}
                        // 是否显示加载框 0不开启
                        isTableLoading={staffDataStore.loading}
                        // 排序方法
                        handleSort={handleSort}
                    />
                </Card>
            </Box>

    )
})

export default StaffDataPage